import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Box, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Button,
    TextField, Alert, Tooltip, Stack,
    useMediaQuery, useTheme, Autocomplete, Chip, createFilterOptions, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import FormDialog, { FormField, filledInputSx } from '../components/FormDialog';
import { Currency } from '../constants/enums';
import { useTranslation } from 'react-i18next';

const filter = createFilterOptions();

const CreditCards = () => {
    const { t } = useTranslation();
    const { api, preferredCurrency } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [creditCards, setCreditCards] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        last_4_digits: '',
        currency: preferredCurrency,
        exchange_rate_fee: 0,
        statement_day: '',
        due_day: '',
        related_category_spending: []
    });
    const [saving, setSaving] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [cardsRes, catsRes] = await Promise.all([
                api.get('/credit-cards/'),
                api.get('/categories/')
            ]);
            setCreditCards(cardsRes.data);
            setCategories(catsRes.data);
            setError(null);
        } catch (err) {
            setError(t('Failed to fetch data.'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [api, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const resetForm = useCallback(() => {
        setFormData({
            name: '',
            last_4_digits: '',
            currency: preferredCurrency,
            exchange_rate_fee: 0,
            statement_day: '',
            due_day: '',
            related_category_spending: []
        });
        setEditingCard(null);
    }, [preferredCurrency]);

    const handleOpenDialog = (card = null) => {
        if (card) {
            setEditingCard(card);
            setFormData({
                name: card.name,
                last_4_digits: card.last_4_digits,
                currency: card.currency || preferredCurrency,
                exchange_rate_fee: card.exchange_rate_fee || 0,
                statement_day: card.statement_day || '',
                due_day: card.due_day || '',
                related_category_spending: card.related_category_spending || []
            });
        } else {
            resetForm();
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        resetForm();
    };

    const handleSave = async () => {
        if (!formData.name.trim() || !formData.last_4_digits.trim()) return;
        setSaving(true);
        // Clean up empty strings to null for integers
        const payload = {
            ...formData,
            statement_day: formData.statement_day === '' ? null : parseInt(formData.statement_day, 10),
            due_day: formData.due_day === '' ? null : parseInt(formData.due_day, 10),
        };

        try {
            if (editingCard) {
                await api.put(`/credit-cards/${editingCard.id}`, payload);
                showToast(t("Credit card updated!"), "success");
            } else {
                await api.post('/credit-cards/', payload);
                showToast(t("Credit card created!"), "success");
            }
            await fetchData();
            handleCloseDialog();
        } catch (err) {
            console.error("Failed to save credit card", err);
            if (err.response?.data?.detail) {
                showToast(typeof err.response.data.detail === 'string' ? err.response.data.detail : JSON.stringify(err.response.data.detail), "error");
            } else {
                showToast(t("An error occurred while saving."), "error");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("Are you sure you want to delete this credit card?"))) return;
        try {
            await api.delete(`/credit-cards/${id}`);
            showToast(t("Credit card deleted."), "success");
            await fetchData();
        } catch (err) {
            console.error("Failed to delete credit card", err);
            if (err.response?.data?.detail) {
                showToast(typeof err.response.data.detail === 'string' ? err.response.data.detail : "Error", "error");
            }
        }
    };


    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <LoadingOverlay open={loading} />
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/')} sx={{ mr: { xs: 1, sm: 2 } }} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {t('Credit Cards')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                        width: { xs: '100%', sm: 'auto' },
                        py: { xs: 1.2, sm: 1 }
                    }}
                >
                    {t('Add Credit Card')}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {isMobile ? (
                <Stack spacing={2}>
                    {creditCards.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid #E2E8F0' }}>
                            <Typography color="text.secondary">{t('No credit cards found.')}</Typography>
                        </Paper>
                    ) : (
                        creditCards.map((row) => (
                            <Paper key={row.id} elevation={0} sx={{ p: 2, border: '1px solid #E2E8F0' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {row.name} (..{row.last_4_digits})
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {row.currency} | {t('Fee:')} {row.exchange_rate_fee}%
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary" aria-label={`${t('Edit')} ${row.name}`}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(row.id)} color="error" aria-label={`${t('Delete')} ${row.name}`}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Paper>
                        ))
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'background.default' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{t('Card Name')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t('Last 4')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{t('Currency')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {creditCards.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {row.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>*{row.last_4_digits}</TableCell>
                                    <TableCell>{row.currency} ({row.exchange_rate_fee}%)</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                            <Tooltip title={t("Edit")}>
                                                <IconButton size="small" onClick={() => handleOpenDialog(row)} sx={{ mr: 1, color: 'primary.main' }} aria-label={`${t('Edit')} ${row.name}`}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title={t("Delete")}>
                                                <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: 'error.main' }} aria-label={`${t('Delete')} ${row.name}`}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {creditCards.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        {t("No credit cards found.")}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <FormDialog
                open={openDialog}
                onClose={saving ? undefined : handleCloseDialog}
                onSubmit={handleSave}
                title={editingCard ? t('Edit Credit Card') : t('Add Credit Card')}
                saveLabel={editingCard ? t('Update') : t('Add Card')}
                saving={saving}
                saveDisabled={!formData.name.trim() || !formData.last_4_digits.trim() || saving}
                maxWidth="sm"
            >
                <Stack spacing={3}>
                    <FormField label={t("Card Name")} required>
                        <TextField
                            autoFocus
                            variant="filled"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            sx={filledInputSx}
                            InputProps={{ disableUnderline: true }}
                            inputProps={{ 'aria-label': t('Card Name') }}
                        />
                    </FormField>

                    <FormField label={t("Last 4 Digits")} required>
                        <TextField
                            variant="filled"
                            fullWidth
                            required
                            value={formData.last_4_digits}
                            onChange={(e) => setFormData({ ...formData, last_4_digits: e.target.value })}
                            sx={filledInputSx}
                            InputProps={{ disableUnderline: true }}
                            inputProps={{ 'aria-label': t('Last 4 Digits'), maxLength: 4, inputMode: 'numeric' }}
                        />
                    </FormField>

                    <Stack direction="row" spacing={3}>
                        <FormField label={t("Currency")} id="card-currency" required sx={{ flex: 1 }}>
                            <Autocomplete
                                id="card-currency"
                                options={[Currency.CAD, Currency.USD, Currency.CNY]}
                                value={formData.currency || Currency.CAD}
                                onChange={(e, newValue) => {
                                    if (newValue) {
                                        setFormData(prev => ({ ...prev, currency: newValue }));
                                    }
                                }}
                                disableClearable
                                autoHighlight
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        fullWidth
                                        sx={filledInputSx}
                                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                                        inputProps={{ ...params.inputProps, 'aria-label': t('Currency') }}
                                    />
                                )}
                            />
                        </FormField>
                        <FormField label={t("FX Fee (%)")} id="fx-fee" sx={{ flex: 1 }}>
                            <TextField
                                id="fx-fee"
                                type="number"
                                variant="filled"
                                fullWidth
                                value={formData.exchange_rate_fee}
                                onChange={(e) => setFormData({ ...formData, exchange_rate_fee: e.target.value })}
                                sx={filledInputSx}
                                InputProps={{ disableUnderline: true }}
                                inputProps={{ 'aria-label': t('FX Fee (%)'), step: "0.01", inputMode: 'decimal' }}
                            />
                        </FormField>
                    </Stack>

                    <Stack direction="row" spacing={3}>
                        <FormField label={t("Statement Day")} id="statement-day" sx={{ flex: 1 }}>
                            <TextField
                                id="statement-day"
                                select
                                variant="filled"
                                fullWidth
                                value={formData.statement_day}
                                onChange={(e) => setFormData({ ...formData, statement_day: e.target.value })}
                                sx={filledInputSx}
                                SelectProps={{ disableUnderline: true }}
                                inputProps={{ 'aria-label': t('Statement Day') }}
                            >
                                <MenuItem value=""><em>{t("None")}</em></MenuItem>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                ))}
                            </TextField>
                        </FormField>
                        <FormField label={t("Due Day")} id="due-day" sx={{ flex: 1 }}>
                            <TextField
                                id="due-day"
                                select
                                variant="filled"
                                fullWidth
                                value={formData.due_day}
                                onChange={(e) => setFormData({ ...formData, due_day: e.target.value })}
                                sx={filledInputSx}
                                SelectProps={{ disableUnderline: true }}
                                inputProps={{ 'aria-label': t('Due Day') }}
                            >
                                <MenuItem value=""><em>{t("None")}</em></MenuItem>
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                    <MenuItem key={day} value={day}>{day}</MenuItem>
                                ))}
                            </TextField>
                        </FormField>
                    </Stack>

                    <FormField label={t("Related Category Spending")} id="related-tags">
                        <Autocomplete
                            id="related-tags"
                            multiple
                            freeSolo
                            options={Array.from(new Set(categories.map(c => c.name))).sort((a, b) => (t(a) || a).localeCompare(t(b) || b, undefined, { sensitivity: 'base' }))}
                            value={formData.related_category_spending || []}
                            onChange={(event, newValue) => {
                                const updatedValues = newValue.map((item) => {
                                    if (typeof item === 'string') return item;
                                    if (item && item.inputValue) return item.inputValue;
                                    return item.title || item.name || '';
                                }).filter(v => v);
                                setFormData({ ...formData, related_category_spending: updatedValues });
                            }}
                            filterOptions={(options, params) => {
                                const filtered = filter(options, params);
                                const { inputValue } = params;
                                const isExisting = options.some((option) => inputValue === option);
                                if (inputValue !== '' && !isExisting) {
                                    filtered.push({
                                        inputValue,
                                        title: `${t('Add')} "${inputValue}"`,
                                    });
                                }
                                return filtered;
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                if (option.inputValue) return option.inputValue;
                                return option.title || option.name || '';
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip 
                                        variant="outlined" 
                                        label={typeof option === 'string' ? option : option.inputValue || option.title} 
                                        {...getTagProps({ index })}
                                        sx={{ borderRadius: '8px', fontWeight: 500 }}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    placeholder={t("Select or type a new tag")}
                                    sx={filledInputSx}
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    inputProps={{ ...params.inputProps, 'aria-label': t('Related Category Spending') }}
                                />
                            )}
                        />
                    </FormField>
                </Stack>
            </FormDialog>
        </Container>
    );
};

export default CreditCards;
